import type { ThemeFieldDescriptor, ThemeStyleToken } from './theme-types';

/** Single source of truth: theme token -> default (HSL components, rem string, or empty for optional). */
export const THEME_STYLE_DEFAULTS: Record<ThemeStyleToken, string> = {
  shell: '',
  field: '',
  background: '0 0% 100%',
  foreground: '225 15% 16%',
  card: '0 0% 100%',
  'card-foreground': '225 15% 16%',
  popover: '0 0% 100%',
  'popover-foreground': '225 15% 16%',
  primary: '210 30% 25%',
  'primary-foreground': '0 0% 100%',
  secondary: '210 15% 94%',
  'secondary-foreground': '210 30% 25%',
  muted: '210 10% 96%',
  'muted-foreground': '220 10% 45%',
  accent: '210 15% 94%',
  'accent-foreground': '210 30% 25%',
  destructive: '0 84.2% 60.2%',
  'destructive-foreground': '0 0% 100%',
  border: '220 13% 90%',
  input: '220 13% 90%',
  ring: '210 30% 25%',
  'chart-1': '210 50% 50%',
  'chart-2': '260 50% 55%',
  'chart-3': '150 50% 45%',
  'chart-4': '325 50% 55%',
  'chart-5': '45 80% 55%',
  radius: '0.625rem',
  'font-body': '',
  'font-heading': '',
};

/**
 * All style tokens in a stable order. Use this instead of `Object.keys(styles)` so the loop variable
 * is typed as `ThemeStyleToken` (`Object.keys` is only `string[]` in TypeScript by design).
 */
export const THEME_STYLE_TOKENS: readonly ThemeStyleToken[] = Object.keys(
  THEME_STYLE_DEFAULTS,
) as ThemeStyleToken[];

/**
 * Default dark palette for the embedded SDK (`DEFAULT_DARK_THEME_STYLES` in sdk-reach-admin).
 * Partner style overrides are merged on top when previewing or when `themeConfig.darkMode` is true.
 */
export const THEME_DARK_STYLE_DEFAULTS: { [K in ThemeStyleToken]: string } = {
  shell: '',
  field: '',
  background: '240 6% 10%',
  foreground: '0 0% 98%',
  card: '240 6% 12%',
  'card-foreground': '0 0% 98%',
  popover: '240 6% 12%',
  'popover-foreground': '0 0% 98%',
  primary: '210 30% 88%',
  'primary-foreground': '240 6% 10%',
  secondary: '240 4% 20%',
  'secondary-foreground': '0 0% 98%',
  muted: '240 4% 16%',
  'muted-foreground': '220 10% 65%',
  accent: '210 15% 24%',
  'accent-foreground': '0 0% 98%',
  destructive: '0 62% 54%',
  'destructive-foreground': '0 0% 98%',
  border: '240 4% 20%',
  input: '240 4% 20%',
  ring: '210 30% 88%',
  'chart-1': '210 50% 70%',
  'chart-2': '260 50% 75%',
  'chart-3': '150 50% 65%',
  'chart-4': '325 50% 75%',
  'chart-5': '45 80% 75%',
  radius: '0.625rem',
  'font-body': '',
  'font-heading': '',
};

export const COLOR_STYLE_FIELDS: ThemeFieldDescriptor[] = [
  { key: 'shell', label: 'Shell (outer chrome)' },
  { key: 'background', label: 'Background' },
  { key: 'foreground', label: 'Foreground' },
  { key: 'card', label: 'Card' },
  { key: 'card-foreground', label: 'Card Foreground' },
  { key: 'popover', label: 'Popover' },
  { key: 'popover-foreground', label: 'Popover Foreground' },
  { key: 'primary', label: 'Primary' },
  { key: 'primary-foreground', label: 'Primary Foreground' },
  { key: 'secondary', label: 'Secondary' },
  { key: 'secondary-foreground', label: 'Secondary Foreground' },
  { key: 'muted', label: 'Muted' },
  { key: 'muted-foreground', label: 'Muted Foreground' },
  { key: 'accent', label: 'Accent' },
  { key: 'accent-foreground', label: 'Accent Foreground' },
  { key: 'destructive', label: 'Destructive' },
  { key: 'destructive-foreground', label: 'Destructive Foreground' },
  { key: 'border', label: 'Border' },
  { key: 'input', label: 'Input border' },
  { key: 'field', label: 'Field (control fill)' },
  { key: 'ring', label: 'Ring' },
];

export const CHART_COLOR_FIELDS: ThemeFieldDescriptor[] = [
  { key: 'chart-1', label: 'Chart 1' },
  { key: 'chart-2', label: 'Chart 2' },
  { key: 'chart-3', label: 'Chart 3' },
  { key: 'chart-4', label: 'Chart 4' },
  { key: 'chart-5', label: 'Chart 5' },
];

export const PREVIEW_FONT_BODY_FALLBACK = 'Inter, system-ui, sans-serif';
export const PREVIEW_FONT_HEADING_FALLBACK = 'Plus Jakarta Sans, system-ui, sans-serif';

/** Tokens that shadow another token when not explicitly set. Used by `theme-helpers`. */
export const STYLE_FALLBACKS: Partial<Record<ThemeStyleToken, ThemeStyleToken>> = {
  shell: 'background',
  field: 'background',
};
